/**
 * Hermes Card - Animated card component with optional module accent
 *
 * Features:
 * - Smooth elevation changes on hover
 * - Optional module color accent bar (left side, 4px)
 * - Purposeful motion using spring physics
 * - 8px grid system spacing
 */

import React from 'react';
import { Card as MuiCard, CardProps as MuiCardProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

export type ModuleAccent = 'courier' | 'santa' | 'email' | 'primary' | 'secondary' | 'none';

export interface HermesCardProps extends Omit<MuiCardProps, 'color'> {
  /**
   * Module color for the accent bar
   */
  accent?: ModuleAccent;

  /**
   * Whether to show hover effect
   * @default true
   */
  hoverable?: boolean;

  /**
   * Custom padding (in 8px units)
   * @default 3
   */
  padding?: number;

  /**
   * Children to render inside the card
   */
  children?: React.ReactNode;
}

// Styled card with accent bar support
const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => prop !== 'accent' && prop !== 'hoverable' && prop !== 'padding',
})<{ accent?: ModuleAccent; hoverable?: boolean; padding?: number }>(
  ({ theme, accent, hoverable, padding = 3 }) => {
    const accentColor =
      accent && accent !== 'none'
        ? (theme.palette[accent as keyof typeof theme.palette] as any)?.main
        : undefined;

    return {
      position: 'relative',
      borderRadius: 16,
      padding: theme.spacing(padding),
      backgroundColor: theme.palette.background.paper,
      transition: 'none', // Disable default transitions (using Framer Motion)
      boxShadow: theme.shadows[1],

      // Accent bar on the left
      ...(accentColor && {
        borderLeft: `4px solid ${accentColor}`,
      }),

      // Optional hover effect
      ...(hoverable && {
        cursor: 'pointer',
        '&:hover': {
          boxShadow: theme.shadows[3],
        },
      }),

      // Preserve box shadow during animations
      willChange: 'transform, box-shadow',
    };
  }
);

// Animation variants for Framer Motion
const cardVariants = {
  rest: {
    y: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 24,
    },
  },
  hover: {
    y: -4,
    scale: 1.005,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 24,
    },
  },
  tap: {
    scale: 0.995,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 17,
    },
  },
};

const noMotionVariants = {
  rest: {},
  hover: {},
  tap: {},
};

export const Card = React.forwardRef<HTMLDivElement, HermesCardProps>(
  ({ accent = 'none', hoverable = true, padding = 3, children, onClick, ...props }, ref) => {
    const isInteractive = hoverable && (onClick !== undefined);

    // Check for reduced motion preference
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const variants = prefersReducedMotion ? noMotionVariants : cardVariants;

    return (
      <motion.div
        initial="rest"
        whileHover={isInteractive ? 'hover' : 'rest'}
        whileTap={isInteractive ? 'tap' : 'rest'}
        variants={variants}
        style={{ display: 'block' }}
      >
        <StyledCard
          ref={ref}
          accent={accent}
          hoverable={hoverable}
          padding={padding}
          onClick={onClick}
          {...props}
        >
          {children}
        </StyledCard>
      </motion.div>
    );
  }
);

Card.displayName = 'HermesCard';

export default Card;
